import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { PageURLMaster } from "../APIs/APIUrls";

const normalizeToken = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const resolvePageUrlPath = (rawUrl) => {
  const url = (rawUrl || "").toString().trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname || "";
    } catch {
      return url.replace(/^https?:\/\//i, "");
    }
  }
  return url.startsWith("/") ? url : `/${url}`;
};

const isReservedRoutePath = (pathname) => {
  const normalized = pathname.replace(/^\/|\/$/g, "");
  if (!normalized) return true;

  const reservedSingleSegment = [
    "home",
    "contact-us",
    "privacy-policy",
    "about-us",
    "our-services",
    "blog-grid",
    "404",
    "dashboard",
    "add-property",
    "add-photos",
    "add-amenities",
    "add-floor-plan",
    "add-document",
    "add-near-amenities",
    "my-profile",
    "buy-subscription",
    "manage-enquiry",
  ];

  if (reservedSingleSegment.includes(normalized)) {
    return true;
  }

  const reservedMultiSegment = [
    /^property-details\/[\w-]+$/,
    /^author\/[\w-]+$/,
    /^blog-detail\/[\w-]+$/,
  ];

  return reservedMultiSegment.some((pattern) => pattern.test(normalized));
};

const SearchResultPageSchema = () => {
  const { pathname } = useLocation();
  const [pageUrlMasterData, setPageUrlMasterData] = useState([]);
  const [loading, setLoading] = useState(true);

  const pathSegments = pathname.split("/").filter(Boolean);
  const title = pathSegments.length === 1 ? pathSegments[0] : null;
  const shouldRenderSchema =
    !!title &&
    pathSegments.length === 1 &&
    !isReservedRoutePath(pathname);

  useEffect(() => {
    const loadPageUrlMasterData = async () => {
      try {
        const cached = localStorage.getItem("PageURLMasterListforWeb");
        let rows = Array.isArray(cached ? JSON.parse(cached) : null)
          ? JSON.parse(cached)
          : [];

        if (!rows.length) {
          const response = await fetch(`${PageURLMaster}GetAllPageURLMasterforWeb`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const result = await response.json();
            rows = result?.data || result?.Data || [];
          }
        }

        if (Array.isArray(rows)) {
          setPageUrlMasterData(rows);
          if (!cached) {
            localStorage.setItem("PageURLMasterListforWeb", JSON.stringify(rows));
          }
        }
      } catch (error) {
        console.error("Error loading SearchResultsPage schema data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPageUrlMasterData();
  }, []);

  if (!shouldRenderSchema || pathname === "/404" || loading) {
    return null;
  }

  const currentUrl = `https://askmerealty.in${pathname}`;
  const normalizedTitle = normalizeToken(title || pathname);

  const matchedPage = pageUrlMasterData.find((page) => {
    const pageUrlPath = normalizeToken(
      resolvePageUrlPath(page?.PageURL || page?.PageUrl || page?.pageURL),
    );
    
    const pageTitle = normalizeToken(
      page?.PageTitle || page?._objDetailPage?.Title || page?.PageName,
    );

    return pageUrlPath === normalizedTitle || pageTitle === normalizedTitle;
  });

  if (!matchedPage) {
    return null;
  }

  const humanName = title
    ? title.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "Search Results";

  const name =
    matchedPage?._objMeta?.Title ||
    matchedPage?.MetaTitle ||
    matchedPage?.PageTitle ||
    matchedPage?.PageName ||
    `${humanName} - AskMe Realty`;

  const description =
    matchedPage?._objMeta?.Description ||
    matchedPage?.MetaDescription ||
    `Search results for ${humanName}. Filter by price, size, bedrooms, and property type. Find your perfect home with AskMe Realty.`;

  const breadcrumbName =
    matchedPage?._objDetailPage?.Title ||
    matchedPage?.PageTitle ||
    matchedPage?.PageName ||
    humanName;

  const schema = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    name,
    description,
    url: currentUrl,
    isPartOf: {
      "@type": "WebSite",
      name: "AskMe Realty",
      url: "https://askmerealty.in/",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://askmerealty.in/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: breadcrumbName,
          item: currentUrl,
        },
      ],
    },
    numberOfItems: "0",
    potentialAction: {
      "@type": "SearchAction",
      target: currentUrl,
      "query-input": "required name=bedrooms",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default SearchResultPageSchema;
